import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Box } from "lucide-react";
import { presentationApi } from "../../../services/presentationApi";
import { useAssetUrl } from "./assets";
import { C, DISPLAY } from "./theme";

// Jumeaux des slides (bloc « twin », zone visuelle) : `embeds` = { "embed:ID": { poster, filename } } reçu avec le
// deck ; `live` : la page du jumeau tourne dans une iframe isolée (slide ouverte, vue en grand). Sinon (miniatures,
// mesure, PDF), son image fixe.
export const EmbedsContext = createContext({ embeds: {}, live: false });

const idOf = (ref) => (typeof ref === "string" && ref.startsWith("embed:") ? ref.slice(6) : null);

// Le portail client lit la page du jumeau par ses propres routes (version publiée) plutôt que par l'API interne.
let fetchDocument = (embedId) => presentationApi.embedDocument(embedId);
export const setEmbedFetcher = (fetcher) => {
  const previous = fetchDocument;
  fetchDocument = fetcher;
  return previous;
};

const LiveFrame = ({ embed, title }) => {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let active = true;
    setPage(null);
    setError(false);
    fetchDocument(idOf(embed)).then((text) => { if (active) setPage(text); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [embed]);
  if (error) return <Frame label="Jumeau indisponible" />;
  if (!page) return <Frame label="Chargement du jumeau…" />;
  return <SizedFrame page={page} title={title} />;
};

// La page n'est posée qu'une fois l'iframe à sa taille : posée tout de suite, elle démarre à 0 × 0 et une page qui
// dimensionne son canvas au démarrage reste vide si l'événement « resize » se perd (fenêtre de présentation en
// arrière-plan ou partagée en visio). Deux images : la mise en page, puis la taille transmise à l'iframe ; la
// minuterie prend le relais quand le navigateur suspend les images d'une fenêtre cachée.
const SizedFrame = ({ page, title }) => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (ready) return undefined;
    let frame = 0;
    const sized = () => ref.current && ref.current.offsetWidth > 0 && ref.current.offsetHeight > 0;
    const check = () => {
      frame = requestAnimationFrame(() => {
        if (sized()) frame = requestAnimationFrame(() => setReady(true));
        else check();
      });
    };
    check();
    const timer = setInterval(() => { if (sized()) setReady(true); }, 400);
    return () => { cancelAnimationFrame(frame); clearInterval(timer); };
  }, [ready]);
  // Origine opaque (pas de allow-same-origin) : ni session, ni stockage, ni accès à Deepflow.
  return (
    <iframe ref={ref} title={title || "Jumeau 3D"} sandbox="allow-scripts" srcDoc={ready ? page : undefined}
      style={{ width: "100%", height: "100%", border: 0, display: "block", background: C.white }} />
  );
};

const Frame = ({ label, children }) => (
  <div style={{ width: "100%", height: "100%", background: C.pale, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 8, color: C.muted, fontSize: 15, textAlign: "center" }}>
    <Box style={{ width: 36, height: 36, color: C.blue }} />
    <div style={{ fontFamily: DISPLAY, fontSize: 13, fontWeight: 700, color: C.blue, letterSpacing: 1.2 }}>JUMEAU 3D</div>
    <div>{label}</div>
    {children}
  </div>
);

const Poster = ({ poster, filename }) => {
  const url = useAssetUrl(poster);
  if (poster && url) return <img src={url} alt={filename || "Jumeau 3D"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />;
  return <Frame label={filename || "Image fixe à venir"} />;
};

// Le jumeau à sa place : vivant ou en image fixe. `poster` (image choisie) prime sur la capture du serveur.
export const TwinView = ({ embed, poster, style }) => {
  const { embeds, live } = useContext(EmbedsContext);
  const info = (embeds || {})[embed] || {};
  return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: 10, border: `1.5px solid ${C.line}`, ...style }}>
      {!embed ? <Frame label="Jumeau à ajouter (réglages du bloc)" />
        : live ? <LiveFrame embed={embed} title={info.filename} />
          : <Poster poster={poster || info.poster} filename={info.filename} />}
    </div>
  );
};
